import {
  ensureProjDef,
  getCogCmPerPx,
  getEpsgCode,
  windowToCoordinates,
  type CogGeoTransform,
} from "./cogOrtho";
import { fromCogUrl } from "./fromCogUrl";
import type { ImageCornerCoordinates } from "./orthoAssets";

/** Metadata that can be read from a COG header without decoding pixels. */
export interface OrthoHeaderMeta {
  /** Full-image footprint corners in WGS84. */
  boundary: ImageCornerCoordinates;
  /** Ground sample distance; null when the CRS is not projected in meters. */
  cmPerPx: number | null;
}

/**
 * Reads the COG header once and extracts every piece of ortho metadata we
 * surface (footprint, cm/px). Does not decode overview pixels.
 */
export const loadOrthoMeta = async (url: string): Promise<OrthoHeaderMeta> => {
  const tiff = await fromCogUrl(url);
  const image = await tiff.getImage();
  const geoKeys = image.getGeoKeys();
  if (geoKeys == null) {
    throw new Error("COG is missing geo keys");
  }

  const epsgCode = getEpsgCode(geoKeys);
  ensureProjDef(epsgCode);

  const [originX, originY] = image.getOrigin();
  const [resolutionX, resolutionY] = image.getResolution();
  const transform: CogGeoTransform = {
    epsgCode,
    height: image.getHeight(),
    originX,
    originY,
    resolutionX,
    resolutionY,
    samplesPerPixel: image.getSamplesPerPixel(),
    width: image.getWidth(),
  };

  return {
    boundary: windowToCoordinates(
      transform,
      0,
      0,
      transform.width,
      transform.height,
    ),
    cmPerPx: getCogCmPerPx(image),
  };
};
